import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusCircleIcon,
    MagnifyingGlassIcon,
    ArrowPathIcon,
    ChevronDownIcon,
    BriefcaseIcon,
    ClockIcon,
    UsersIcon,
    UserIcon,
    PhoneIcon,
    DocumentTextIcon,
    CalendarIcon
} from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Mock data for a complete recruitment pipeline
const mockPipeline = {
    'Sourced': [
        { id: 'candidate-1', name: 'John Doe', role: 'Frontend Developer', appliedDate: '2024-07-20' },
        { id: 'candidate-2', name: 'Jane Smith', role: 'UI/UX Designer', appliedDate: '2024-07-18' },
    ],
    'Screening': [
        { id: 'candidate-3', name: 'Emily White', role: 'Backend Developer', appliedDate: '2024-07-22' },
    ],
    'Interview': [
        { id: 'candidate-4', name: 'David Lee', role: 'Data Scientist', appliedDate: '2024-07-15' },
    ],
    'Offer': [
        { id: 'candidate-5', name: 'Sarah Chen', role: 'Product Manager', appliedDate: '2024-07-10' },
    ],
    'Hired': []
};

const stages = ['Sourced', 'Screening', 'Interview', 'Offer', 'Hired'];

const RecruitmentPipeline = () => {
    const [pipeline, setPipeline] = useState(mockPipeline);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState('All');
    const [showRoleDropdown, setShowRoleDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Get all unique roles for the filter dropdown
    const allRoles = useMemo(() => {
        const roles = new Set();
        Object.values(mockPipeline).flat().forEach(candidate => roles.add(candidate.role));
        return ['All', ...Array.from(roles)];
    }, []);

    // Filtered candidates based on search term and selected role
    const filteredPipeline = useMemo(() => {
        const newPipeline = {};
        for (const stage of stages) {
            newPipeline[stage] = pipeline[stage].filter(candidate => {
                const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesRole = selectedRole === 'All' || candidate.role === selectedRole;
                return matchesSearch && matchesRole;
            });
        }
        return newPipeline;
    }, [pipeline, searchTerm, selectedRole]);

    const onDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const startStage = source.droppableId;
        const endStage = destination.droppableId;
        const newPipeline = { ...pipeline };
        const [movedCandidate] = newPipeline[startStage].splice(source.index, 1);

        newPipeline[endStage].splice(destination.index, 0, movedCandidate);
        setPipeline(newPipeline);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-teal-100 to-lime-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header & Controls */}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 pb-4 border-b-2 border-green-400">
                    <h1 className="text-4xl font-extrabold text-green-800 tracking-tight mb-4 md:mb-0">
                        Recruitment Pipeline
                    </h1>
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search candidates..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-300 shadow-sm"
                            />
                        </div>
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                                className="flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 rounded-full border border-gray-300 shadow-sm hover:bg-gray-100 transition-colors"
                            >
                                <BriefcaseIcon className="h-5 w-5" />
                                <span>{selectedRole}</span>
                                <ChevronDownIcon className="h-4 w-4 ml-1" />
                            </button>
                            <AnimatePresence>
                                {showRoleDropdown && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className="absolute right-0 mt-2 w-48 rounded-lg shadow-xl bg-white z-10"
                                    >
                                        <ul className="py-1">
                                            {allRoles.map(role => (
                                                <li
                                                    key={role}
                                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                                    onClick={() => {
                                                        setSelectedRole(role);
                                                        setShowRoleDropdown(false);
                                                    }}
                                                >
                                                    {role}
                                                </li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>

                {/* Main Kanban Board */}
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6 overflow-x-auto pb-4">
                        {stages.map(stage => (
                            <Droppable key={stage} droppableId={stage}>
                                {(provided) => (
                                    <motion.div
                                        className="bg-white rounded-3xl shadow-xl p-6 min-w-[300px] flex-grow"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <div className={`flex items-center justify-between pb-4 mb-4 border-b-2 ${
                                            stage === 'Sourced' ? 'border-purple-500' :
                                            stage === 'Screening' ? 'border-indigo-500' :
                                            stage === 'Interview' ? 'border-blue-500' :
                                            stage === 'Offer' ? 'border-orange-500' :
                                            'border-green-500'
                                        }`}>
                                            <h3 className="text-xl font-bold text-gray-800">{stage} ({filteredPipeline[stage].length})</h3>
                                            <div className="flex items-center space-x-2">
                                                <UsersIcon className="h-5 w-5 text-gray-500" />
                                            </div>
                                        </div>
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className="min-h-[200px] space-y-4"
                                        >
                                            <AnimatePresence>
                                                {filteredPipeline[stage].length > 0 ? (
                                                    filteredPipeline[stage].map((candidate, index) => (
                                                        <Draggable key={candidate.id} draggableId={candidate.id} index={index}>
                                                            {(provided, snapshot) => (
                                                                <motion.div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    initial={{ opacity: 0, y: 20 }}
                                                                    animate={{ opacity: 1, y: 0 }}
                                                                    exit={{ opacity: 0, scale: 0.9 }}
                                                                    transition={{ duration: 0.3 }}
                                                                    className={`bg-gray-50 rounded-xl p-4 shadow-sm border ${
                                                                        snapshot.isDragging ? 'border-blue-500 shadow-md' : 'border-gray-200'
                                                                    } hover:bg-gray-100 transition-colors cursor-pointer`}
                                                                >
                                                                    <h4 className="font-semibold text-gray-900">{candidate.name}</h4>
                                                                    <p className="text-sm text-gray-600 flex items-center mt-1">
                                                                        <BriefcaseIcon className="h-4 w-4 mr-1 text-gray-400" />
                                                                        {candidate.role}
                                                                    </p>
                                                                    <p className="text-xs text-gray-500 flex items-center mt-1">
                                                                        <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" />
                                                                        Applied: {candidate.appliedDate}
                                                                    </p>
                                                                    <div className="mt-4 flex justify-end space-x-2">
                                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-blue-500 hover:text-blue-700 transition-colors">
                                                                            <UserIcon className="h-5 w-5" />
                                                                        </motion.button>
                                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-green-500 hover:text-green-700 transition-colors">
                                                                            <PhoneIcon className="h-5 w-5" />
                                                                        </motion.button>
                                                                        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} className="text-purple-500 hover:text-purple-700 transition-colors">
                                                                            <DocumentTextIcon className="h-5 w-5" />
                                                                        </motion.button>
                                                                    </div>
                                                                </motion.div>
                                                            )}
                                                        </Draggable>
                                                    ))
                                                ) : (
                                                    <div className="p-4 text-center text-gray-400 border-dashed border-2 border-gray-300 rounded-lg">
                                                        <p>No candidates in this stage.</p>
                                                    </div>
                                                )}
                                            </AnimatePresence>
                                            {provided.placeholder}
                                        </div>
                                    </motion.div>
                                )}
                            </Droppable>
                        ))}
                    </div>
                </DragDropContext>
            </motion.div>
        </div>
    );
};

export default RecruitmentPipeline;